import Length from "./Length";
import Rectangle from "./Rectangle";
import Area from "./Area";
import RightAngledTriangle from "./RightAngledTriangle";
import AbstractShape from "./AbstractShape";

export default class AbstractRightAngledTriangle extends AbstractShape {
	constructor( base, height ) {
		super();

		this.base = base;
		this.height = height;
	}

	/**
	 * Gets the enclosing rectangle for the triangle.
	 *
	 * @return {Rectangle} The enclosing rectangle.
	 */
	get_enclosing_rectangle() {
		return new Rectangle( this.base, this.height );
	}

	/**
	 * Gets the total area for the triangle.
	 *
	 * @return {Area} The area.
	 */
	get_area() {
		const area_in_mm2 = 0.5 * this.base.in_mm() * this.height.in_mm();
		return Area.from_mm2( area_in_mm2 );
	}

	/**
	 * Gets the circumference of the triangle.
	 *
	 * @return {Length} The circumference.
	 */
	get_circumference() //Pythagorean theorem, a^2 + b^2 = c^2
	{
		const a = this.base.in_mm();
		const b = this.height.in_mm();
		const c = Math.sqrt( Math.pow( a, 2 ) + Math.pow( b, 2 ) );
		return Length.from_mm( a + b + c );
	}

	/**
	 * Gets the base.
	 *
	 * @return {number} The base length.
	 */
	get_base() {
		return this.base;
	}

	/**
	 * Gets the height.
	 *
	 * @return {number} The height.
	 */
	get_height() {
		return this.height;
	}

	/**
	 * Gets the maximum dimensions.
	 *
	 * @param {Rectangle} maximum_rectangle The maximum rectangle.
	 *
	 * @return {{base: *, height: *}} The maximum dimensions.
	 */
	get_maximum_dimensions( maximum_rectangle ) // Shapes can be ordered in the maximum sheet size minus 2.5cm on every side.
	{
		return {
			base: maximum_rectangle.get_length(),
			height: maximum_rectangle.get_width()
		};
	}

	/**
	 * Gets the dimension keys.
	 *
	 * @return {string[]} The dimension keys.
	 */
	get_dimension_keys() {
		return ["base", "height"];
	}

	/**
	 * Gets the modifier keys.
	 *
	 * @return {*[]} The modifier keys.
	 */
	get_modifier_keys() {
		return [];
	}

	/**
	 * Serializes the primitives.
	 *
	 * @return {{base, height}} The serialized primitives.
	 */
	serialize_primitives() {
		return {
			base: this.base.in_mm(),
			height: this.base.in_mm()
		};
	}

	/**
	 * Deserializes the primitives to create a new instance.
	 *
	 * @param {object} primitives The primitives.
	 *
	 * @return {RightAngledTriangle} The instance.
	 */
	static deserialize_primitives( primitives ) {
		return new RightAngledTriangle( new Length( primitives.base ), new Length( primitives.height ) );
	}

}
